import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { loadSectionTexts } from "../../utils/contentService";

// Reusable Checkbox Component
const Checkbox = ({ label, checked, onClick }) => (
  <div
    className="flex items-center gap-3 md:gap-4 cursor-pointer group"
    onClick={onClick}
  >
    <div
      className={`w-5 h-5 border border-black flex items-center justify-center flex-shrink-0 transition-colors duration-200 ${checked ? "bg-black group-hover:bg-gray-800" : "bg-transparent group-hover:bg-gray-100"
        }`}
    >
      {checked && (
        <svg
          width="14"
          height="10"
          viewBox="0 0 14 10"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1 5L4.5 8.5L13 1"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </div>
    <span className="text-black text-lg md:text-3xl font-thin font-serif leading-tight md:leading-[50px]">
      {label}
    </span>
  </div>
);

// Error Message Component
const ErrorMessage = ({ message, texts }) => {
  if (!message) return null;
  // Look up the error message from texts using the key
  const displayMessage = texts[message] || message;
  return (
    <p className="text-red-600 text-sm md:text-base font-serif mt-2 flex items-center gap-2">
      <svg
        className="w-4 h-4 flex-shrink-0"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fillRule="evenodd"
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
          clipRule="evenodd"
        />
      </svg>
      <span>{displayMessage}</span>
    </p>
  );
};

// Helper to get a value from flat dot-notation keys
const gt = (texts, key) => texts[key] || "";

export default function ConfirmationForm({
  formData,
  errors = {},
  handleChange,
  handleSelection,
  handleChildrenCountChange,
  handleChildAgeChange,
  handleSubmit,
  loading,
}) {
  const { i18n } = useTranslation();
  const [texts, setTexts] = useState({});

  useEffect(() => {
    setTexts(loadSectionTexts("confirmation", i18n.language));
  }, [i18n.language]);

  return (
    <div className="mx-auto max-w-[1100px] px-4 py-8 md:py-[60px]">
      <form onSubmit={handleSubmit} className="space-y-12 md:space-y-[100px]">
        {/* Attendance Status - MOVED TO TOP */}
        <div className="flex flex-col gap-6 md:gap-8">
          <h2 className="text-black text-2xl md:text-4xl font-medium font-serif leading-tight text-left">
            {gt(texts, "attendance.title")}
          </h2>
          <div className="space-y-8 md:space-y-12 w-full">
            {/* Will you come */}
            <div>
              <label className="block text-black text-lg md:text-3xl font-normal font-serif mb-4 md:mb-8 leading-tight">
                {gt(texts, "attendance.question")}
              </label>
              <div className="flex flex-wrap gap-6 md:gap-[100px]">
                <Checkbox
                  label={gt(texts, "attendance.yes")}
                  checked={formData.attendance === "yes"}
                  onClick={() => handleSelection("attendance", "yes")}
                />
                <Checkbox
                  label={gt(texts, "attendance.no")}
                  checked={formData.attendance === "no"}
                  onClick={() => handleSelection("attendance", "no")}
                />
              </div>
              <ErrorMessage message={errors.attendance} texts={texts} />
            </div>

            {/* Side of Invitation */}
            <div>
              <label className="block text-black text-lg md:text-3xl font-normal font-serif mb-4 md:mb-8 leading-tight">
                {gt(texts, "personalInfo.invitationSide")}
              </label>
              <div className="flex flex-wrap gap-6 md:gap-[100px]">
                <Checkbox
                  label={gt(texts, "personalInfo.bride")}
                  checked={formData.invitationSide === "bride"}
                  onClick={() => handleSelection("invitationSide", "bride")}
                />
                <Checkbox
                  label={gt(texts, "personalInfo.groom")}
                  checked={formData.invitationSide === "groom"}
                  onClick={() => handleSelection("invitationSide", "groom")}
                />
                <Checkbox
                  label={gt(texts, "personalInfo.both")}
                  checked={formData.invitationSide === "both"}
                  onClick={() => handleSelection("invitationSide", "both")}
                />
              </div>
              <ErrorMessage message={errors.invitationSide} texts={texts} />
            </div>
          </div>
        </div>

        {/* Personal Information */}
        <div className="flex flex-col gap-6 md:gap-8">
          <h2 className="text-black text-2xl md:text-4xl font-medium font-serif leading-tight text-left">
            {gt(texts, "personalInfo.title")}
          </h2>
          <div className="space-y-6 md:space-y-[70px]">
            <div>
              <input
                type="text"
                name="name"
                placeholder={gt(texts, "personalInfo.name")}
                value={formData.name}
                onChange={handleChange}
                className={`w-full bg-transparent border-b ${errors.name ? "border-red-600" : "border-black"
                  } focus:outline-none font-serif text-lg md:text-3xl py-2 md:py-1 placeholder:text-black placeholder:font-thin placeholder:opacity-60`}
              />
              <ErrorMessage message={errors.name} texts={texts} />
            </div>
            <div>
              <input
                type="email"
                name="email"
                placeholder={gt(texts, "personalInfo.email")}
                value={formData.email}
                onChange={handleChange}
                className={`w-full bg-transparent border-b ${errors.email ? "border-red-600" : "border-black"
                  } focus:outline-none font-serif text-lg md:text-3xl py-2 md:py-1 placeholder:text-black placeholder:font-thin placeholder:opacity-60`}
              />
              <ErrorMessage message={errors.email} texts={texts} />
            </div>
            <div>
              <input
                type="tel"
                name="phone"
                placeholder={gt(texts, "personalInfo.phone")}
                value={formData.phone}
                onChange={handleChange}
                className={`w-full bg-transparent border-b ${errors.phone ? "border-red-600" : "border-black"
                  } focus:outline-none font-serif text-lg md:text-3xl py-2 md:py-1 placeholder:text-black placeholder:font-thin placeholder:opacity-60`}
              />
              <ErrorMessage message={errors.phone} texts={texts} />
            </div>
          </div>
        </div>

        {/* Additional Guests */}
        <div className="flex flex-col gap-6 md:gap-8">
          <h2 className="text-black text-2xl md:text-4xl font-medium font-serif leading-tight text-left">
            {gt(texts, "guests.title")}
          </h2>
          <div className="space-y-8 md:space-y-12 w-full">
            <div>
              <label className="block text-black text-lg md:text-3xl font-normal font-serif mb-4 md:mb-8 leading-tight">
                {gt(texts, "guests.plusOne")}
              </label>
              <div className="flex flex-wrap gap-6 md:gap-[140px]">
                <Checkbox
                  label={gt(texts, "guests.yes")}
                  checked={formData.plusOne === true}
                  onClick={() => handleSelection("plusOne", true)}
                />
                <Checkbox
                  label={gt(texts, "guests.no")}
                  checked={formData.plusOne === false}
                  onClick={() => handleSelection("plusOne", false)}
                />
              </div>
              <ErrorMessage message={errors.plusOne} texts={texts} />
              {formData.plusOne === true && (
                <div className="mt-4 md:mt-6">
                  <input
                    type="text"
                    name="plusOneCount"
                    placeholder={gt(texts, "guests.plusOneCount")}
                    value={formData.plusOneCount}
                    onChange={handleChange}
                    className={`w-full md:max-w-[500px] bg-transparent border-b ${errors.plusOneCount ? "border-red-600" : "border-black"
                      } focus:outline-none font-serif text-base md:text-2xl py-2 md:py-1 placeholder:text-black placeholder:font-thin placeholder:opacity-60`}
                  />
                  <ErrorMessage message={errors.plusOneCount} texts={texts} />
                </div>
              )}
            </div>
            <div>
              <label className="block text-black text-lg md:text-3xl font-normal font-serif mb-4 md:mb-8 leading-tight">
                {gt(texts, "guests.children")}
              </label>
              <div className="flex flex-wrap gap-6 md:gap-[140px]">
                <Checkbox
                  label={gt(texts, "guests.yes")}
                  checked={formData.children === true}
                  onClick={() => handleSelection("children", true)}
                />
                <Checkbox
                  label={gt(texts, "guests.no")}
                  checked={formData.children === false}
                  onClick={() => handleSelection("children", false)}
                />
              </div>
              <ErrorMessage message={errors.children} texts={texts} />
              {formData.children === true && (
                <div className="mt-6 md:mt-8 space-y-6 md:space-y-8">
                  {/* Counter */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 md:gap-6">
                    <span className="text-black text-base md:text-3xl font-thin font-serif leading-tight">
                      {gt(texts, "guests.childrenCount")}
                    </span>
                    <div className="flex items-center gap-3 md:gap-4">
                      <button
                        type="button"
                        onClick={() => handleChildrenCountChange(-1)}
                        className="w-8 h-8 md:w-10 md:h-10 border border-black flex items-center justify-center text-xl md:text-2xl hover:bg-black hover:text-white transition-colors"
                        disabled={formData.childrenCount <= 1}
                      >
                        -
                      </button>
                      <span className="text-black text-lg md:text-3xl font-normal w-8 text-center">
                        {formData.childrenCount}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleChildrenCountChange(1)}
                        className="w-8 h-8 md:w-10 md:h-10 border border-black flex items-center justify-center text-xl md:text-2xl hover:bg-black hover:text-white transition-colors"
                        disabled={formData.childrenCount >= 5}
                      >
                        +
                      </button>
                    </div>
                  </div>

                  {/* Age Inputs */}
                  <div className="space-y-4 md:space-y-6">
                    {Array.from({ length: formData.childrenCount }).map(
                      (_, index) => (
                        <div key={index}>
                          <input
                            type="text"
                            placeholder={(gt(texts, "guests.childAge") || "Child {{number}} age").replace("{{number}}", index + 1)}
                            value={formData.childrenAges[index] || ""}
                            onChange={(e) =>
                              handleChildAgeChange(index, e.target.value)
                            }
                            className={`w-full md:max-w-[500px] bg-transparent border-b ${errors[`childAge${index}`]
                              ? "border-red-600"
                              : "border-black"
                              } focus:outline-none font-serif text-base md:text-2xl py-2 md:py-1 placeholder:text-black placeholder:font-thin placeholder:opacity-60 block`}
                          />
                          <ErrorMessage message={errors[`childAge${index}`]} texts={texts} />
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Special Requirements */}
        <div className="flex flex-col gap-6 md:gap-8">
          <h2 className="text-black text-2xl md:text-4xl font-medium font-serif leading-tight text-left">
            {gt(texts, "requirements.title")}
          </h2>
          <div className="w-full">
            <textarea
              name="requirements"
              placeholder={gt(texts, "requirements.placeholder")}
              value={formData.requirements}
              onChange={handleChange}
              className={`w-full h-40 md:h-40 bg-transparent border ${errors.requirements ? "border-red-600" : "border-black"
                } p-3 md:p-4 font-serif text-base md:text-3xl focus:outline-none resize-none placeholder:text-black placeholder:font-thin placeholder:opacity-60 box-border`}
            />
            <ErrorMessage message={errors.requirements} texts={texts} />

            <div className="mt-8 md:mt-16 flex justify-center md:justify-start">
              <button
                type="submit"
                disabled={loading}
                className="w-64 h-16 bg-blue1 hover:bg-blue text-white rounded-xl font-serif italic transition-all duration-300 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="text-xl md:text-2xl font-normal">
                  {loading
                    ? gt(texts, "sending")
                    : gt(texts, "submit")}
                </span>
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
